import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}
function pickArr(res: any): any[] {
  const d = pick(res);
  return Array.isArray(d) ? d : [];
}

export const examCallsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ── MEMBER ─────────────────────────────────────────────── */

    getAvailableExamCalls: builder.query<any[], void>({
      query: () => "/exam-calls",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((c: any) => ({ type: "ExamCalls" as const, id: c.id })),
              { type: "ExamCalls" as const, id: "LIST" },
            ]
          : [{ type: "ExamCalls" as const, id: "LIST" }],
    }),

    applyToExamCall: builder.mutation<
      any,
      { callId: string; exam_center_id?: string; cpd_attested?: boolean }
    >({
      query: ({ callId, exam_center_id, cpd_attested }) => ({
        url: `/exam-calls/${callId}/apply`,
        method: "POST",
        body: {
          ...(exam_center_id ? { exam_center_id } : {}),
          // The candidate's declaration that they hold this exam's CPD. Sent
          // only when they have actually made it — an absent flag and a false
          // one mean the same thing to the server, and sending false on every
          // ordinary apply would read as a declaration having been declined.
          ...(cpd_attested ? { cpd_attested: true } : {}),
        },
      }),
      invalidatesTags: [
        { type: "ExamCalls" as const, id: "LIST" },
        { type: "MyExamCallApps" as const, id: "LIST" },
      ],
    }),

    getMyExamCallApplications: builder.query<any[], void>({
      query: () => "/my-exam-applications",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a: any) => ({ type: "MyExamCallApps" as const, id: a.id })),
              { type: "MyExamCallApps" as const, id: "LIST" },
            ]
          : [{ type: "MyExamCallApps" as const, id: "LIST" }],
    }),

    submitExamCallApplication: builder.mutation<any, string>({
      query: (id) => ({
        url: `/exam-applications/${id}/submit`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "MyExamCallApps" as const, id: "LIST" },
        { type: "MyExamCallApps" as const, id },
      ],
    }),

    // Exam-call flow: apply -> pay -> (paid) -> finalize (center + papers/courses).

    // Start the OnePay payment for a draft application; returns the OnePay
    // redirect payload (submit via submitOnePayForm).
    // Takes a bare id, or the id with the CPD declaration the candidate made to
    // get past this exam's CPD bar. The union keeps every existing caller — the
    // declaration only exists on the one screen that offers it.
    initiateExamPayment: builder.mutation<
      any,
      string | { id: string; cpd_attested?: boolean }
    >({
      query: (arg) => {
        const { id, cpd_attested } = typeof arg === "string" ? { id: arg, cpd_attested: false } : arg;

        return {
          url: `/exam-applications/${id}/pay/initiate`,
          method: "POST",
          body: cpd_attested ? { cpd_attested: true } : {},
        };
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: "MyExamCallApps" as const, id: typeof arg === "string" ? arg : arg.id },
      ],
    }),

    // Ask the API to verify this application's payment with OnePay right now,
    // in the request, rather than waiting on the queued job the gateway
    // callback dispatches — a stalled worker would otherwise leave a paid
    // member stuck on the processing page indefinitely.
    settleExamPayment: builder.mutation<any, string>({
      query: (id) => ({
        url: `/exam-applications/${id}/pay/settle`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, id) => [{ type: "MyExamCallApps" as const, id }],
    }),

    // Papers (outstanding) + courses to choose from when finalizing a paid app.
    getExamFinalizeOptions: builder.query<any, string>({
      query: (id) => `/exam-applications/${id}/finalize-options`,
      transformResponse: (res: any) => res ?? {},
      // Tagged so saving the documents re-reads what is still
      // outstanding. That list gates the Submit button, and a stale copy would
      // either block a candidate who has just finished or let one through who
      // has not.
      providesTags: (_r, _e, id) => [{ type: "MyExamCallApps" as const, id }],
    }),

    finalizeExamApplication: builder.mutation<
      any,
      {
        id: string;
        // Optional: an exam that is not a sitting has no centre to name, and
        // the field is omitted rather than sent empty.
        exam_center_id?: string;
        exam_paper_ids?: string[];
        exam_course_ids?: string[];
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/exam-applications/${id}/finalize`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "MyExamCallApps" as const, id: "LIST" },
        { type: "MyExamCallApps" as const, id: arg.id },
      ],
    }),

    // Hand the completed form to the secretariat. Locks the application until
    // it is approved, returned or rejected.
    submitExamApplicationForReview: builder.mutation<any, string>({
      query: (id) => ({
        url: `/exam-applications/${id}/submit-for-review`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "MyExamCallApps" as const, id },
        { type: "MyExamCallApps" as const, id: "LIST" },
        { type: "MyExamCallApps" as const, id: `form:${id}` },
      ],
    }),

    // The NIQS application form (TPC/PCI). GET returns the saved form, or a
    // draft pre-filled from the member record + probationer application when
    // nothing has been saved yet — `prefilled` says which.
    getExamApplicationForm: builder.query<any, string>({
      query: (id) => `/exam-applications/${id}/form`,
      transformResponse: (res: any) => res ?? {},
      providesTags: (_r, _e, id) => [{ type: "MyExamCallApps" as const, id: `form:${id}` }],
    }),

    // `body` is normally the form document as JSON. Where a qualification
    // certificate has been chosen it is a FormData instead, carrying the same
    // document in a `payload` field with the files alongside — fetchBaseQuery
    // detects FormData and drops the JSON content-type so the browser can set
    // its own multipart boundary.
    saveExamApplicationForm: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/exam-applications/${id}/form`,
        method: "POST",
        body,
        formData: body instanceof FormData,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "MyExamCallApps" as const, id: `form:${arg.id}` },
        { type: "MyExamCallApps" as const, id: arg.id },
      ],
    }),

    // The hand-in: whichever documents the exam's `requires_*` flags ask for —
    // O'level, academic qualifications, workshop certificate, technical report,
    // attestation letter — plus the declared graduation year and the CPD
    // confirmation. The server says which apply, so this shape is the same for
    // every exam and the screen renders what it is told.
    //
    // GET also returns what can be pre-filled — O'level and academic
    // qualifications from the member's Institute record, falling back to their
    // probationer application — with a `*_source` saying which store answered.
    getExamApplicationDocuments: builder.query<any, string>({
      query: (id) => `/exam-applications/${id}/documents`,
      transformResponse: (res: any) => res ?? {},
      providesTags: (_r, _e, id) => [{ type: "MyExamCallApps" as const, id: `documents:${id}` }],
    }),

    // Multipart — the uploads and each O'level scan ride along as files, so the
    // caller hands over a FormData and the browser sets its own boundary.
    saveExamApplicationDocuments: builder.mutation<any, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/exam-applications/${id}/documents`,
        method: "POST",
        body,
        formData: true,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "MyExamCallApps" as const, id: `documents:${arg.id}` },
        { type: "MyExamCallApps" as const, id: arg.id },
      ],
    }),

    // Section 9 — resolve a sponsor's membership number to their particulars.
    // A mutation rather than a query because it fires on demand, when the
    // candidate asks for it, not whenever the number in the box changes.
    lookupExamSponsor: builder.mutation<any, { id: string; membership_no: string }>({
      query: ({ id, membership_no }) => ({
        url: `/exam-applications/${id}/sponsor-lookup`,
        method: "GET",
        params: { membership_no },
      }),
    }),

    /* ── ADMIN ──────────────────────────────────────────────── */

    adminGetExamCalls: builder.query<any[], void>({
      query: () => "/admin/exam-calls",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((c: any) => ({ type: "AdminExamCalls" as const, id: c.id })),
              { type: "AdminExamCalls" as const, id: "LIST" },
            ]
          : [{ type: "AdminExamCalls" as const, id: "LIST" }],
    }),

    adminGetExamCall: builder.query<any, string>({
      query: (id) => `/admin/exam-calls/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_res, _err, id) => [{ type: "AdminExamCalls" as const, id }],
    }),

    adminCreateExamCall: builder.mutation<any, any>({
      query: (body) => ({
        url: "/admin/exam-calls",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AdminExamCalls" as const, id: "LIST" }],
    }),

    adminUpdateExamCall: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/admin/exam-calls/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminExamCalls" as const, id: "LIST" },
        { type: "AdminExamCalls" as const, id: arg.id },
        { type: "ExamCalls" as const, id: "LIST" },
      ],
    }),

    adminDeleteExamCall: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admin/exam-calls/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "AdminExamCalls" as const, id: "LIST" },
        { type: "AdminExamCalls" as const, id },
        { type: "ExamCalls" as const, id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAvailableExamCallsQuery,
  useApplyToExamCallMutation,
  useGetMyExamCallApplicationsQuery,
  useSubmitExamCallApplicationMutation,
  useInitiateExamPaymentMutation,
  useSettleExamPaymentMutation,
  useGetExamFinalizeOptionsQuery,
  useFinalizeExamApplicationMutation,
  useSubmitExamApplicationForReviewMutation,
  useGetExamApplicationFormQuery,
  useSaveExamApplicationFormMutation,
  useGetExamApplicationDocumentsQuery,
  useSaveExamApplicationDocumentsMutation,
  useLookupExamSponsorMutation,
  useAdminGetExamCallsQuery,
  useAdminGetExamCallQuery,
  useAdminCreateExamCallMutation,
  useAdminUpdateExamCallMutation,
  useAdminDeleteExamCallMutation,
} = examCallsApi;
